import Vue from "vue";

const getDefaultState = () => {
  return {
    openArea: {
      areaID: "",
      areaType: "",
      areaName: "",
      areaData: null,
      loading: false,
    },
    openItem: null,
    openItemIndexInItems: 0,
  };
};

const state = getDefaultState();

/* Sort objects array by _id field to match order of same id values in string array of ids */
function sortItemsByIDs(items, ids) {
  return items.sort((a, b) => ids.indexOf(a._id) - ids.indexOf(b._id));
  // orderedIDs.map((i) => items.find((j) => j._id === i));
  // items.sort((a, b) => orderedIDs.indexOf(a._id) > orderedIDs.indexOf(b._id));
}

function updateItemsOrder(areaData) {
  let items = [];
  if (areaData && areaData.items && areaData.items.length && areaData.list && areaData.list.itemIDs && areaData.list.itemIDs.length) {
    /* Order items by array of orderedIDs */
    items = sortItemsByIDs(areaData.items, areaData.list.itemIDs);
  }
  return items;
}

// Synchronous
const mutations = {
  RESET_STATE(state) {
    // Merge rather than replace so we don't lose observers
    // https://github.com/vuejs/vuex/issues/1118
    Object.assign(state, getDefaultState());
  },

  SET_OPEN_AREA(state, area) {
    state.openItem = null;
    state.openItemIndexInItems = 0;
    const openArea = state.openArea;
    openArea.areaID = area.areaID;
    openArea.areaType = area.areaType;
    openArea.areaName = area.areaName;
    openArea.loading = true;
    if (openArea.areaData) {
      Vue.set(openArea, "areaData", null);
    }
  },

  SET_OPEN_AREA_DATA(state, areaData) {
    /* Set initial order of items in list. Done here rather than
    through computed as complications arose when keeping computed
    synced with changes to orderedIDs array. */
    areaData.items = updateItemsOrder(areaData);
    state.openArea.areaData = areaData;
    state.openArea.loading = false;
  },

  ADD_NEW_ITEM_TO_LIST(state, itemData) {
    if (state.openArea.areaType == "list" && itemData.areaID === state.openArea.areaID && itemData.item) {
      const item = itemData.item;
      const areaData = state.openArea.areaData;
      areaData.items.push(item);
      areaData.list.itemIDs.push(item._id);
      if (item.properties && item.properties.length) {
        areaData.listProperties = [...areaData.listProperties, ...item.properties];
      }
    }
  },

  UPDATE_ITEMS_ORDER_IN_LIST(state, orderData) {
    const openArea = state.openArea;
    if (openArea.areaType == "list" && orderData.areaID == openArea.areaID) {
      openArea.areaData.list.itemIDs = orderData.newOrderedIDs;
      openArea.areaData.items = updateItemsOrder(openArea.areaData);
    }
  },

  /* Remove item from items and also from list's itemIDs */
  REMOVE_ITEM(state, itemData) {
    const openArea = state.openArea;
    if (openArea.areaType == "list" && itemData.areaID == openArea.areaID) {
      /* Remove item from items array */
      openArea.areaData.items.splice(itemData.itemIndexInItems, 1);

      /* Remove itemID from array of itemIDs */
      openArea.areaData.list.itemIDs.splice(itemData.itemIndexInList, 1);
    }
  },

  ADD_ITEM_BACK(state, itemData) {
    const openArea = state.openArea;
    if (openArea.areaType == "list" && itemData.areaID == openArea.areaID) {
      /* Remove item from items array */
      openArea.areaData.items.splice(itemData.itemIndexInItems, 0, itemData.item);

      /* Remove itemID from array of itemIDs */
      openArea.areaData.list.itemIDs.splice(itemData.itemIndexInList, 0, itemData.item);
    }
  },

  SET_OPEN_ITEM(state, item) {
    state.openItem = item;
    const items = state.openArea.areaData.items;
    state.openItemIndexInItems = items.findIndex((el) => el._id === item._id);
  },

  UPDATE_OPEN_ITEM_DATA(state, itemData) {
    /* Update open item's field value */
    Vue.set(state.openItem, itemData.field, itemData.value);

    /* Update equivalent item's value in Items list */
    Vue.set(state.openArea.areaData.items[state.openItemIndexInItems], itemData.field, itemData.value);
  },

  SET_LIST_SKIP_VALUE(state, listData) {
    if (state.openArea.areaData.list._id == listData.listID) {
      state.openArea.areaData.list.skipValue = listData.skipValue;
    }
  },

  SET_LIST_LOADED_STATE(state, listData) {
    if (state.openArea.areaData.list._id == listData.listID) {
      state.openArea.areaData.list.allLoaded = listData.allLoaded;
    }
  },

  /* Re-order items according to current view and order of itemIDs */
  ADD_LOADED_ITEMS_TO_LIST(state, itemsData) {
    if (state.openArea.areaData.list._id == itemsData.listID) {
      /* Concatenate existing array of items with new array of items */
      let items = [...state.openArea.areaData.items, ...itemsData.loadedItems];

      /* Get array of ordered item IDs */
      const orderedIDs = state.openArea.areaData.list.itemIDs;

      /* Order items by array of orderedIDs */
      items = sortItemsByIDs(items, orderedIDs);

      /* Set new and updated array in Vuex */
      state.openArea.areaData.items = items;
    }
  },
};

// Asynchronous
const actions = {
  resetState({ commit }) {
    commit("RESET_STATE");
  },

  setOpenArea({ commit }, area) {
    commit("SET_OPEN_AREA", area);
  },

  setOpenAreaData({ commit }, areaData) {
    commit("SET_OPEN_AREA_DATA", areaData);
  },

  addNewItemToList({ commit }, itemData) {
    commit("ADD_NEW_ITEM_TO_LIST", itemData);
  },

  updateItemsOrderInList({ commit }, orderData) {
    commit("UPDATE_ITEMS_ORDER_IN_LIST", orderData);
  },

  removeItem({ commit }, itemData) {
    commit("REMOVE_ITEM", itemData);
  },

  addItemBack({ commit }, itemData) {
    commit("ADD_ITEM_BACK", itemData);
  },

  setOpenItem({ commit }, item) {
    commit("SET_OPEN_ITEM", item);
  },

  updateOpenItemData({ commit }, itemData) {
    commit("UPDATE_OPEN_ITEM_DATA", itemData);
  },

  setListSkipValue({ commit }, listData) {
    commit("SET_LIST_SKIP_VALUE", listData);
  },

  setListLoadedState({ commit }, listData) {
    commit("SET_LIST_LOADED_STATE", listData);
  },

  addLoadedItemsToList({ commit }, itemsData) {
    commit("ADD_LOADED_ITEMS_TO_LIST", itemsData);
  },
};

const openArea = {
  namespaced: true,

  state,
  mutations,
  actions,
};

export default openArea;
